import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, GraviGrid, Texto, validateInt } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { sectorCreateConfig } from './createConfig'
import { Popover, Tooltip, TreeSelect } from 'antd'
import { ApartmentOutlined } from '@ant-design/icons'
import { InNetworkTreeSelectEditor } from '@components/_SiteWide/Grid/CellEditors/InNetworkTerminalsTreeSelect'

export function SectorGrid({ data, metadata }) {
  const onSectorCreate = useCallback((sector) => {
    return api.createSector({ ...sector, market_id: data.id }).then(() => Promise.resolve(true))
  }, [])
  const gridApiRef = useRef()

  const checkInNetworkTerminalsList = (sectorList, marketList) => {
    if (sectorList) {
      // if market list exists and is equal to the length of the sector list
      if (marketList?.length > 0 && marketList.length === sectorList.length) {
        // check if there are unique values in sector list
        const uniqueValues = sectorList.filter((name, index) => {
          const found = marketList.find((terminal) => terminal === name)
          return found === undefined
        })
        // if there are no unique values, return false
        if (uniqueValues?.length === 0) {
          return false
        }
        return true
        //else return true
      } else {
        return true
      }
    }
  }
  const onSectorUpdate = useCallback((sector) => {
    if (sector?.in_network_terminal_names?.length === 0) {
      const check = checkInNetworkTerminalsList(sector.in_network_terminal_names, data.in_network_terminal_names)
      if (!check) {
        sector.in_network_terminal_names = []
      }
    }
    sector.in_network_terminals = sector.in_network_terminal_names

    const ret = api
      .updateSector({
        ...sector,
        market_id: data.id,
        trailer_config: sector?.trailer_config === 'None' ? null : sector?.trailer_config,
      })
      .then((data) => {
        sector.only_in_network_terminal_names = data.data.sector_in_network_terminal_names
        sector.in_network_supply_zone_names = data.data.sector_in_network_supply_zone_names

        const node = gridApiRef?.current?.getRowNode(sector.id)
        gridApiRef?.current?.refreshCells({ rowNodes: [node], force: true })
      })

    return ret.then(() => Promise.resolve(true))
  }, [])
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.location_write
  const agPropOverrides = useMemo(
    () => ({
      rowGroupPanelShow: 'never',
      columnDefaultOverrides: { enableRowGroup: false, filter: false },
      getRowId: (row) => row?.data?.id,
    }),
    []
  )
  const treeData = useMemo(() => {
    if (!metadata?.supply_zones) return []
    const supplyZonesArray = Object.entries(metadata?.supply_zones)
    const list = data?.in_network_terminal_names || []
    return supplyZonesArray.map(([parent, children]) => {
      return {
        title: parent,
        value: parent,
        key: parent,
        children: children.map((child) => {
          if (list.includes(child)) {
            return {
              title: (
                <Texto>
                  {child} <ApartmentOutlined style={{ color: 'var(--theme-color-2)' }} className={'ml-1'} />
                </Texto>
              ),
              value: child,
              key: child,
            }
          }
          return { title: child, value: child, key: child }
        }),
      }
    })
  }, [metadata?.supply_zones, data?.in_network_terminal_names])
  const columnDefs = useMemo(
    () => getColumnDefs(metadata, treeData, data, checkInNetworkTerminalsList),
    [metadata, data?.in_network_terminal_names, treeData]
  )

  return (
    <div style={{ width: '100%', height: '100%', padding: '0.5rem' }}>
      <GraviGrid
        controlBarProps={{
          title: 'Market Sectors',
        }}
        createEP={canEdit && onSectorCreate}
        updateEP={canEdit && onSectorUpdate}
        createConfig={sectorCreateConfig}
        createSelectOptions={metadata}
        rowData={data.sectors}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        externalRef={gridApiRef}
      />
    </div>
  )
}

function getFilterOptionsForTerminals(marketMetadata, params) {
  const flat_list = []
  Object.keys(marketMetadata?.supply_zones)?.forEach((zone) => {
    marketMetadata?.supply_zones[zone].forEach((terminal) => {
      flat_list.push(terminal)
    })
  })
  return params.success(flat_list)
}

function getFilterOptionsForSupplyZones(marketMetadata, params) {
  const flat_list = []
  Object.keys(marketMetadata?.supply_zones)?.forEach((zone) => {
    if (zone === 'No Supply Zone') return
    flat_list.push(zone)
  })
  return params.success(flat_list)
}

function customValueFormatter(params) {
  return params.value
}

const getColumnDefs = (metadata, treeData, marketRowData, checkInNetworkTerminalsList) => [
  {
    headerName: 'Name',
    field: 'name',
  },
  {
    headerName: 'Network Radius (miles)',
    field: 'network_radius',
    valueSetter: validateInt,
    valueGetter: (params) => {
      return params.data.network_radius
    },
    valueFormatter: (params) => {
      if (params.data.in_network_terminal_names?.length) {
        return (
          <Tooltip title={`Overridden By In Network Terminals`}>
            <GraviButton
              className={'ghost-gravi-button p-0'}
              style={{ color: 'var(--gray-400)', textAlign: 'left', fontWeight: 'normal' }}
              buttonText={params.data.network_radius}
            />
          </Tooltip>
        )
      }
      return <Texto>{params.data.network_radius}</Texto>
    },
    cellRenderer: (params) => {
      const terminals = params.data
      const text = terminals.network_radius || 'Inherits from Market'
      // debugger
      const isOverriden = checkInNetworkTerminalsList(
        terminals.in_network_terminal_names,
        terminals.market_in_network_terminal_names
      )
      if (isOverriden && terminals.in_network_terminal_names.length !== 0) {
        return (
          <Tooltip title={`Overridden By In Network Terminals`}>
            <GraviButton
              className={'ghost-gravi-button p-0'}
              style={{ color: 'var(--gray-400)', textAlign: 'left', fontWeight: 'normal' }}
              buttonText={text}
            />
          </Tooltip>
        )
      }
      return <Texto>{text}</Texto>
    },
  },
  {
    headerName: 'Trailer Config',
    field: 'trailer_config',
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: [...metadata.trailer_configs, 'None'],
    },
    rules: [
      {
        required: true,
        message: 'Trailer configuration required',
      },
    ],
  },
  {
    editable: true,
    headerName: 'In-Network Terminals',
    field: 'in_network_terminal_names',
    minWidth: 300,
    cellEditor: InNetworkTreeSelectEditor,
    cellEditorParams: {
      treeData,
      supply_zones: metadata?.supply_zones,
      defaultList: marketRowData.in_network_terminal_names,
      width: 275,
    },
    cellRenderer: (params) => {
      if (params?.data?.in_network_terminal_names?.length) {
        const terminals = params.data.only_in_network_terminal_names
        const supplyZones = params.data.in_network_supply_zone_names
        const hasTerminals = terminals?.length > 0
        const hasSupplyZones = supplyZones?.length > 0

        const content = (
          <>
            {hasSupplyZones && <Texto weight='bold'>Supply Zones:</Texto>}
            {supplyZones?.map((supplyZone) => (
              <Texto key={supplyZone}>{supplyZone}</Texto>
            ))}
            {hasTerminals && hasSupplyZones && <br />}
            {hasTerminals && <Texto weight='bold'>Terminals:</Texto>}
            {terminals?.map((terminal) => (
              <Texto key={terminal}>{terminal}</Texto>
            ))}
          </>
        )

        return (
          <Popover content={content} placement='topLeft'>
            <GraviButton buttonText={params?.data?.in_network_terminal_names.length} />
          </Popover>
        )
      }

      return <Texto>Inherits from Market</Texto>
    },
    valueFormatter: (params) => {
      if (params.node?.group) {
        const node = params?.node?.childrenAfterGroup[0]
        const terminals = node?.data?.only_in_network_terminal_names.join(', ')
        const supplyZones = node?.data?.in_network_supply_zone_names.join(', ')

        if (!terminals && !supplyZones) {
          return 'No In Network Terminals'
        }

        if (!terminals) {
          return supplyZones
        }

        if (!supplyZones) {
          return terminals
        }

        return `${supplyZones} --- ${terminals}`
      }

      return params.value
    },
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          title: 'Filter By Supply Zone',
          filterParams: {
            field: 'number',
            buttons: ['reset'],
            values: (params) => getFilterOptionsForSupplyZones(metadata, params),
            valueGetter: (params) => {
              return params?.data?.in_network_supply_zone_names
            },
            valueFormatter: customValueFormatter,
          },
        },
        {
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          title: 'Filter By Terminal',
          filterParams: {
            showTooltips: true,
            field: 'type',
            values: (params) => getFilterOptionsForTerminals(metadata, params),
            valueGetter: ({ data }) => {
              return data?.in_network_terminal_names
            },
            valueFormatter: customValueFormatter,
            buttons: ['reset'],
          },
        },
      ],
    },
  },
]
